import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * v2 · 05 Badges — pills only. No square badges, no mono type.
 *
 * 13/500 Inter in sentence case, full radius, 8px 14px padding.
 * Pink is earned: stock and confirmations, nothing else.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-2 rounded-full text-[13px] font-medium leading-none py-2 px-3.5',
  {
    variants: {
      variant: {
        /** Neutral fact stated with authority — raised fill. */
        ink: 'border border-hairline bg-surface-sunk text-ink',
        /** In stock · scarcity — the pink wash with the dot. */
        accent:
          'border border-[color:var(--sss-accent-tint-line)] bg-signal-tint text-signal',
        /** Drop 001 — metadata, not a claim. */
        outline: 'border border-hairline text-ink-muted',
        /** Choose one — an instruction the user still owes us. */
        tint: 'border border-hairline bg-surface-sunk text-ink-muted',
        /** Selected — the pink confirmation. */
        selected:
          'border border-[color:var(--sss-accent-tint-line)] bg-signal-tint text-signal',
        /** Overlay on product media — dark glass so the art reads through. */
        overlay:
          'border border-[rgba(255,255,255,0.12)] bg-[rgba(10,10,10,0.75)] text-white',
      },
    },
    defaultVariants: { variant: 'ink' },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />
}

/**
 * Status pills — admin. Full-radius, hairline outline, 6px dot,
 * 12/500 sentence case. The dot carries the colour so the pill stays
 * legible when statuses stack in a dense table.
 */
const statusPillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border text-[12px] font-medium leading-none py-1.5 px-2.5',
  {
    variants: {
      status: {
        pending: 'border-hairline text-ink-muted',
        paid: 'border-[color:var(--sss-success)] text-[color:var(--sss-success)]',
        shipped:
          'border-[color:var(--sss-accent-tint-line)] bg-signal-tint text-signal',
        refunded: 'border-destructive text-destructive',
      },
    },
    defaultVariants: { status: 'pending' },
  }
)

const statusDot: Record<string, string> = {
  pending: 'bg-ink-faint',
  paid: 'bg-[color:var(--sss-success)]',
  shipped: 'bg-signal',
  refunded: 'bg-destructive',
}

export type OrderStatus = 'pending' | 'paid' | 'shipped' | 'refunded'

interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusPillVariants> {
  status: OrderStatus
}

function StatusPill({ className, status, children, ...props }: StatusPillProps) {
  return (
    <span className={cn(statusPillVariants({ status }), className)} {...props}>
      <span aria-hidden="true" className={cn('size-1.5 rounded-full', statusDot[status])} />
      {children}
    </span>
  )
}

/**
 * Spec chips — the configurator's read-back of what the customer
 * picked. Pills, 13/500 sentence case. `swatch` prepends a 12px
 * colour dot — the name is always shown too, never colour alone.
 */
const chipVariants = cva(
  'inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-medium leading-none',
  {
    variants: {
      variant: {
        default: 'border border-hairline bg-surface text-ink',
        ink: 'border border-[color:var(--sss-field-border)] bg-surface-sunk text-ink',
      },
    },
    defaultVariants: { variant: 'default' },
  }
)

interface ChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  swatch?: string
}

function Chip({ className, variant, swatch, children, ...props }: ChipProps) {
  return (
    <span className={cn(chipVariants({ variant }), className)} {...props}>
      {swatch && (
        <span
          aria-hidden="true"
          className="size-3 rounded-full border border-hairline"
          style={{ background: swatch }}
        />
      )}
      {children}
    </span>
  )
}

export { Badge, badgeVariants, StatusPill, Chip }
